"use client";

import { useCallback, useEffect, useRef } from "react";
import Script from "next/script";
import { useTrackingConsent } from "@/lib/consent/use-tracking-consent";
import { isTrackingAllowed } from "@/lib/consent/privacy";
import { usePathname } from "next/navigation";
import { META_PIXEL } from "@/lib/analytics/config";
import type { ConsentUpdateEvent } from "@/lib/analytics/types";

/**
 * Meta (Facebook) Pixel tracker.
 *
 * Mirrors the XPixelTracker pattern: lazyOnload script injection, consent
 * gating via the existing `consent_update` custom event, soft-fail on
 * adblocker blocks, and a manual `$pageview` fan-out for App Router SPA
 * navigations (mirrors PostHogPageView.tsx).
 *
 * The shared privacy gate controls loading, initial consent, and subsequent
 * updates. GPC and a saved refusal always take precedence over region defaults.
 *
 * The pixel sends one initial PageView from inside the init snippet (after
 * the consent state is set). The pathname-watching useEffect skips its very
 * first render so the initial pageview isn't double-counted, then fires
 * `fbq('track', 'PageView')` on every SPA navigation thereafter.
 */
export default function MetaPixelTracker() {
  const allowed = useTrackingConsent();
  const pathname = usePathname();
  const isFirstRender = useRef(true);

  const handleConsentUpdate = useCallback((event: ConsentUpdateEvent) => {
    if (!window.__metaPixelInitialized || typeof window.fbq !== "function") return;
    try {
      const newState = event.detail.state;
      if (newState === "granted" && isTrackingAllowed()) {
        window.fbq("consent", "grant");
        console.log("Meta pixel enabled after consent");
      } else {
        window.fbq("consent", "revoke");
        console.log("Meta pixel disabled after decline");
      }
    } catch (e) {
      console.error("Error updating Meta pixel consent:", e);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("consent_update", handleConsentUpdate as EventListener);
    return () => {
      window.removeEventListener("consent_update", handleConsentUpdate as EventListener);
    };
  }, [handleConsentUpdate]);

  // SPA pageview fan-out. Skip first render — the init snippet fires the
  // initial PageView itself after consent state is applied.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isTrackingAllowed()) return;
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    if (window.__metaPixelBlocked) return;
    try {
      window.fbq("track", "PageView");
    } catch (e) {
      console.warn("Meta pixel SPA PageView failed:", e);
    }
  }, [pathname]);

  if (!allowed) return null;

  return (
    <Script
      id="meta-pixel"
      strategy="lazyOnload"
      onError={(error) => {
        console.warn("Meta pixel script failed to load (likely blocked by adblocker):", error);
        window.__metaPixelBlocked = true;
      }}
    >
      {`
        (function () {
        if (!window.privacyConsent?.allowed) return;
        try {
          !function(f,b,e,v,n,t,s){
            if(f.fbq)return;
            n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;
            n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];
            t=b.createElement(e);t.async=!0;t.src=v;
            s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s);
          }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');

          // The fbq queue accepts these consent calls
          // before fbevents.js finishes loading and replays them on load.
          window.fbq('consent', window.privacyConsent?.allowed ? 'grant' : 'revoke');
          window.fbq('init', '${META_PIXEL.PIXEL_ID}');
          window.__metaPixelInitialized = true;
          console.log('Meta pixel initialized under shared privacy consent');

          // Initial PageView. Will be tagged with the consent state set above.
          if (window.privacyConsent?.allowed) window.fbq('track', 'PageView');

          // Block-detection timeout — if fbq never becomes callable within
          // 5s, mark as blocked so downstream event tracking soft-fails.
          setTimeout(function() {
            if (typeof window.fbq !== 'function' && !window.__metaPixelBlocked) {
              console.warn('Meta pixel initialization timeout - likely blocked');
              window.__metaPixelBlocked = true;
            }
          }, 5000);
        } catch (error) {
          console.warn('Meta pixel initialization failed:', error);
          window.__metaPixelBlocked = true;
        }
        })();
      `}
    </Script>
  );
}
